// ═══════════════════════════════════════════════════════════════════════════
// grainSummary.js — the ONE view-model behind the Grain Marketing Summary.
//
// The Summary page answers four questions and nothing else:
//   1. How many bushels are we planning around?
//   2. What is already physically committed or price-protected?
//   3. What still needs a decision or a physical home?
//   4. Is there one practical action worth reviewing now?
//
// It COMPOSES the engines that were already right (grainPosition.js's
// exposure, grainCommitment.js's physical-vs-price split, grainStorage.js's
// capacity split, grainMarketingStagePolicy.js's sourced CFFM band,
// futuresContractHistory.js's cross-year comparison) and CORRECTS the four
// places where the page drew a misleading conclusion from a correct number.
// Nothing here re-derives a guarantee, a percentile or a basis.
//
// ── EVERY MATERIAL NUMBER CARRIES A CLASS, AND THE CLASSES ARE NOT PEERS ──
// FARM_FACT        an imported contract, a scale ticket, a confirmed capacity
// FARM_ESTIMATE    a farmer-entered yield, a planned acre, an expected date
// MARKET_OBSERVED  a settle, a dated cash bid, a basis, a stated trend
// MODEL_BENCHMARK  the insurance-derived planning crop, the CFFM band, a
//                  suggested step, a market classification
// A benchmark must never render like a farmer-entered plan, and an observed
// example bid must never render like an executable farm price. The UI reads
// `class` off every figure this file returns; it does not decide for itself.
//
// UNKNOWN NEVER BECOMES ZERO. A missing contract, a missing basis, a missing
// delivery window and a missing production figure each return their own
// stated absence.
//
// PURE — no fetch, no React, no Supabase, no Date.now()/new Date(). The
// caller (App.jsx, the one layer with a real clock) supplies `asOf`.
// ═══════════════════════════════════════════════════════════════════════════

import { roundDownToLot } from "./grainCash";
import { resolveMarketingStagePolicy } from "./grainMarketingStagePolicy";

// ── Truth classes ──────────────────────────────────────────────────────────
export const CLASS = {
  FARM_FACT: "farm_fact",
  FARM_ESTIMATE: "farm_estimate",
  MARKET_OBSERVED: "market_observed",
  MODEL_BENCHMARK: "model_benchmark",
  UNKNOWN: "unknown",
};

export const CLASS_LABEL = {
  [CLASS.FARM_FACT]: "Farm fact",
  [CLASS.FARM_ESTIMATE]: "Farm estimate",
  [CLASS.MARKET_OBSERVED]: "Market observation",
  [CLASS.MODEL_BENCHMARK]: "Model / benchmark",
  [CLASS.UNKNOWN]: "Unknown",
};

const posNum = (v) => (v != null && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null);

function daysBetween(fromYmd, toYmd) {
  const re = /^(\d{4})-(\d{2})-(\d{2})$/;
  const a = re.exec(String(fromYmd || "").trim()), b = re.exec(String(toYmd || "").trim());
  if (!a || !b) return null;
  const ta = Date.UTC(+a[1], +a[2] - 1, +a[3]), tb = Date.UTC(+b[1], +b[2] - 1, +b[3]);
  return Math.round((tb - ta) / 86400000);
}

function fmtAcres(n) {
  if (n == null) return "?";
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? String(r) : String(r);
}

function pad2(n) { return String(Number(n)).padStart(2, "0"); }

function ymd(v) {
  if (!v) return null;
  const s = String(v).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
}

// ═══════════════════════════════════════════════════════════════════════════
// 1. PRODUCTION BASIS — the denominator, and where it came from
// ═══════════════════════════════════════════════════════════════════════════
// Five ranks, strongest first. The strongest AVAILABLE one wins and the page
// prints which. Nothing is silently overwritten: every rank this farm could
// have answered with is returned in `alternatives[]` with its own figure, so
// a reader can see the number that was NOT used and why.
//
// ⚠ RANK 1 REQUIRES AN AFFIRMATIVE COMPLETENESS SIGNAL. `actual_production`
// on grain_crop_plan is a hand-entered bushel figure with no completeness
// column beside it, and scale tickets accumulate through harvest — so a
// partial harvest must never become the final crop. Absent an explicit
// `harvestComplete === true`, an actual figure is REPORTED (as an
// alternative, carrying its own state) and does NOT take the denominator.
// That is the difference between "the crop is in the bin" and "some of it
// is", and no column in this database currently states which.
export const PRODUCTION_SOURCE = {
  ACTUAL_COMPLETE: "actual_complete",
  FARM_ESTIMATE: "farm_estimate",
  AGRONOMIC: "agronomic",
  INSURANCE_DERIVED: "insurance_derived",
  UNKNOWN: "unknown",
};

const PRODUCTION_RANK = {
  [PRODUCTION_SOURCE.ACTUAL_COMPLETE]: 1,
  [PRODUCTION_SOURCE.FARM_ESTIMATE]: 2,
  [PRODUCTION_SOURCE.AGRONOMIC]: 3,
  [PRODUCTION_SOURCE.INSURANCE_DERIVED]: 4,
  [PRODUCTION_SOURCE.UNKNOWN]: 5,
};

// resolveProductionBasis({ actualBu, harvestComplete, planAcres, planYieldPerAcre,
//                          planAphYield, choppedAcres, agronomicBu, insuranceDerivedBu, asOf })
//
//   actualBu           grain_crop_plan.actual_production, or a summed scale-ticket
//                      figure. NEVER final unless harvestComplete === true.
//   harvestComplete    true | false | null(unknown). No column states this today,
//                      so every live caller passes null and rank 1 is unreachable —
//                      by construction, not by accident.
//   planAcres/planYieldPerAcre  the Crop Plan entry. A farmer-entered estimate.
//   planAphYield       the insurance APH the plan row carries, used ONLY to say
//                      whether the entered yield carries information of its own.
//   choppedAcres       acres insured as grain the grower will chop — they deliver
//                      no bushels, so they leave the farm-estimate denominator the
//                      same way grainChopAdjust already removes them from the
//                      insured ceiling.
//   insuranceDerivedBu grainPosition.exposure.expectedBu — RMA's liability-first
//                      guarantee with the coverage haircut backed out.
export function resolveProductionBasis({
  actualBu = null, harvestComplete = null,
  planAcres = null, planYieldPerAcre = null, planAphYield = null, choppedAcres = 0,
  agronomicBu = null, agronomicLabel = null,
  insuranceDerivedBu = null, asOf = null,
} = {}) {
  const alternatives = [];

  const actual = posNum(actualBu);
  if (actual != null) {
    alternatives.push({
      source: PRODUCTION_SOURCE.ACTUAL_COMPLETE, bu: actual,
      class: CLASS.FARM_FACT,
      label: "Actual production entered",
      complete: harvestComplete === true,
      note: harvestComplete === true
        ? "harvest reported complete"
        : "harvest completeness is not stated on this record — a partial harvest is not final production, so this does not replace the planning figure",
    });
  }

  const acres = posNum(planAcres);
  const chopped = Math.max(0, Math.min(acres || 0, Number(choppedAcres) || 0));
  const grainAcres = acres != null ? Math.max(0, acres - chopped) : null;
  const yld = posNum(planYieldPerAcre);
  const farmEstimateBu = (grainAcres != null && yld != null) ? grainAcres * yld : null;
  if (farmEstimateBu != null) {
    const matchesAph = posNum(planAphYield) != null && Math.abs(Number(planAphYield) - yld) < 0.05;
    alternatives.push({
      source: PRODUCTION_SOURCE.FARM_ESTIMATE, bu: farmEstimateBu,
      class: CLASS.FARM_ESTIMATE,
      label: "Crop Plan estimate",
      matchesAph,
      note: `${fmtAcres(grainAcres)} ac x ${yld} bu/ac, entered on the Crop Plan`
        + (matchesAph ? " — equal to the insurance APH, so it carries no yield information the policy did not already state" : "")
        + (chopped > 0 ? ` (${fmtAcres(chopped)} ac insured as grain are planned for chopping and deliver no bushels)` : ""),
    });
  }

  const agro = posNum(agronomicBu);
  if (agro != null) {
    alternatives.push({
      source: PRODUCTION_SOURCE.AGRONOMIC, bu: agro,
      class: CLASS.MODEL_BENCHMARK,
      label: agronomicLabel || "Agronomic yield estimate",
      note: "modelled yield forecast, not a farm entry",
    });
  }

  const ins = posNum(insuranceDerivedBu);
  if (ins != null) {
    alternatives.push({
      source: PRODUCTION_SOURCE.INSURANCE_DERIVED, bu: ins,
      class: CLASS.MODEL_BENCHMARK,
      label: "Insurance-derived estimate",
      note: "RMA's own liability-first guarantee with the coverage-level haircut backed out — a planning figure, not an agronomic forecast",
    });
  }

  // Rank 1 is only SELECTABLE when completeness is affirmed; every other rank
  // is selectable as soon as it has a figure.
  const selectable = alternatives.filter((a) =>
    a.source !== PRODUCTION_SOURCE.ACTUAL_COMPLETE || a.complete === true);
  selectable.sort((a, b) => PRODUCTION_RANK[a.source] - PRODUCTION_RANK[b.source]);
  const chosen = selectable[0] || null;

  if (!chosen) {
    return {
      known: false, bu: null,
      source: PRODUCTION_SOURCE.UNKNOWN, sourceLabel: "Unknown",
      class: CLASS.UNKNOWN,
      note: "no actual production, Crop Plan entry or priceable RMA coverage resolved for this crop and year",
      alternatives, asOf, headline: "Planning production: not known",
      isEstimate: true,
    };
  }

  return {
    known: true,
    bu: chosen.bu,
    source: chosen.source,
    sourceLabel: chosen.label,
    class: chosen.class,
    note: chosen.note,
    isEstimate: chosen.source !== PRODUCTION_SOURCE.ACTUAL_COMPLETE,
    alternatives,
    asOf,
    headline: chosen.source === PRODUCTION_SOURCE.ACTUAL_COMPLETE
      ? "Actual production" : "Planning production",
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 2. RANGE STATE — where the farm sits IN a band, never "how far from the top"
// ═══════════════════════════════════════════════════════════════════════════
// The page used to print "Target range 43-75%, gap to target 47,000 bu" for a
// farm at 42.6%. Both halves were misleading. The low end of that "range" was
// the farm's OWN CURRENT POSITION (grainTargetPosition.js sets
// targetLow = max(currentPct, stage floor)), so "42.6% against 43-75%" was
// tautologically "at the bottom"; and 47,000 bu was the distance to the
// range's CEILING, called a gap as though the ceiling were the target.
//
// This function reports the BENCHMARK band (the stage's own sourced floor and
// ceiling) and names the state. Distance to each boundary is returned
// separately and is never called "the gap".
//
// The low-end / high-end thresholds are STATED PARAMETERS (thirds of the
// band), not economic findings — the same convention every stated window in
// this file family follows.
export const RANGE_STATE = {
  BELOW: "below_range",
  LOW_END: "within_low_end",
  WITHIN: "within_range",
  HIGH_END: "within_high_end",
  ABOVE: "above_range",
  UNKNOWN: "unknown",
};

const RANGE_STATE_LABEL = {
  [RANGE_STATE.BELOW]: "Below the planning range",
  [RANGE_STATE.LOW_END]: "At the low end of the planning range",
  [RANGE_STATE.WITHIN]: "Within the planning range",
  [RANGE_STATE.HIGH_END]: "At the high end of the planning range",
  [RANGE_STATE.ABOVE]: "Above the planning range",
  [RANGE_STATE.UNKNOWN]: "Planning range not known",
};

export function resolveRangePosition({ currentPct, floorPct, ceilingPct } = {}) {
  if (currentPct == null || floorPct == null || ceilingPct == null || !(ceilingPct > floorPct)) {
    return {
      known: false, state: RANGE_STATE.UNKNOWN, label: RANGE_STATE_LABEL[RANGE_STATE.UNKNOWN],
      currentPct: currentPct == null ? null : Number(currentPct),
      floorPct: floorPct == null ? null : Number(floorPct),
      ceilingPct: ceilingPct == null ? null : Number(ceilingPct),
      distanceToFloorPct: null, distanceToCeilingPct: null, spanFraction: null,
    };
  }
  const cur = Number(currentPct);
  let state;
  if (cur < floorPct) state = RANGE_STATE.BELOW;
  else if (cur > ceilingPct) state = RANGE_STATE.ABOVE;
  else {
    const f = (cur - floorPct) / (ceilingPct - floorPct);
    state = f <= 1 / 3 ? RANGE_STATE.LOW_END : (f >= 2 / 3 ? RANGE_STATE.HIGH_END : RANGE_STATE.WITHIN);
  }
  return {
    known: true, state, label: RANGE_STATE_LABEL[state],
    currentPct: cur, floorPct, ceilingPct,
    // Distances, both directions, both named for what they are. NEITHER is
    // "the gap" — a range has two boundaries and a next step is a third thing.
    distanceToFloorPct: +(floorPct - cur).toFixed(1),
    distanceToCeilingPct: +(ceilingPct - cur).toFixed(1),
    spanFraction: +(((cur - floorPct) / (ceilingPct - floorPct))).toFixed(3),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 3. STAGE LABEL — an August page may not print a mid-June objective as if it
//    were today's
// ═══════════════════════════════════════════════════════════════════════════
// grainMarketingStagePolicy.js resolves the CFFM stage correctly. What it
// does NOT do is say whether the stage's own objective DATE has already
// passed — so on 2026-08-28 the card printed the bare stage name
// "midjune objective", which reads as a current-date objective and is 74 days
// stale as one.
//
// It is also an AGENCY PLANNING BENCHMARK, not a plan this farm chose. No
// farmer-entered marketing-plan seam exists anywhere in this app, so
// `planSource` is `agency_benchmark` for every farm today and the UI must
// never say "your target" or "the target".
export const PLAN_SOURCE = { AGENCY_BENCHMARK: "agency_benchmark", FARM_ENTERED: "farm_entered" };

const STAGE_DISPLAY = {
  EARLY_PREHARVEST: "Early pre-harvest",
  MIDJUNE_OBJECTIVE: "Pre-harvest",
  HARVEST_AND_POST: "Harvest and post-harvest",
  PRE_PLAN: "Before the plan starts",
};

export function resolveStageLabel({ crop, cropYear, asOf, farmEnteredPlan = null } = {}) {
  if (farmEnteredPlan && farmEnteredPlan.known) {
    return { ...farmEnteredPlan, planSource: PLAN_SOURCE.FARM_ENTERED, class: CLASS.FARM_ESTIMATE };
  }
  const policy = resolveMarketingStagePolicy({ crop, cropYear, asOf });
  if (!policy.known || !policy.range) {
    return {
      known: false, planSource: PLAN_SOURCE.AGENCY_BENCHMARK, class: CLASS.MODEL_BENCHMARK,
      stage: policy.stage || null,
      stageLabel: policy.stage ? (STAGE_DISPLAY[policy.stage] || policy.stage) : null,
      floorPct: null, ceilingPct: null,
      headline: "No sourced planning benchmark for this crop and date",
      reasons: policy.reasons || [], source: policy.source,
      objective: null, asOf,
    };
  }
  // The one dated objective CFFM states: 75% priced by mid-June. Whether that
  // date is ahead, today, or behind is the thing the bare stage name hid.
  const objectiveDate = `${Number(cropYear)}-06-15`;
  const daysFrom = daysBetween(objectiveDate, asOf);
  const objectiveStatus = daysFrom == null ? "unknown" : (daysFrom < 0 ? "upcoming" : (daysFrom === 0 ? "today" : "past"));
  const stageLabel = STAGE_DISPLAY[policy.stage] || policy.stage;
  const plural = (n) => (Math.abs(n) === 1 ? "" : "s");
  let headline;
  if (policy.stage === "MIDJUNE_OBJECTIVE") {
    // This stage is only ever entered ON or AFTER June 15
    // (grainMarketingStagePolicy.js: md >= midJuneMd), so the objective is
    // always behind by the time it is reached. Saying "mid-June objective" and
    // stopping — which is what the bare stage key did — reads on an August page
    // as though it were today's objective.
    headline = `${stageLabel} — CFFM's mid-June ${policy.range.ceilingPct}% objective was ${daysFrom} day${plural(daysFrom)} ago`;
  } else if (policy.stage === "HARVEST_AND_POST") {
    headline = `${stageLabel} — the pre-harvest plan has ended`;
  } else {
    // EARLY_PREHARVEST: the objective is genuinely ahead, so say how far.
    headline = `${stageLabel} — CFFM caps sales before the mid-June push at ${policy.range.ceilingPct}%`
      // The mid-June objective's own PERCENTAGE is not on this stage's range
      // (which carries the early CAP, a different number), so the countdown
      // names the date and not a figure this object cannot state.
      + (daysFrom != null && daysFrom < 0 ? `; the mid-June objective is ${Math.abs(daysFrom)} day${plural(daysFrom)} away` : "");
  }
  return {
    known: true, planSource: PLAN_SOURCE.AGENCY_BENCHMARK, class: CLASS.MODEL_BENCHMARK,
    stage: policy.stage, stageLabel,
    floorPct: policy.range.floorPct, ceilingPct: policy.range.ceilingPct,
    headline,
    objective: { pct: policy.range.ceilingPct, date: objectiveDate, status: objectiveStatus, daysFrom },
    reasons: policy.reasons || [], source: policy.source,
    asOf,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 4. THE THREE TARGETS, AND THE NEXT STEP — four separate numbers
// ═══════════════════════════════════════════════════════════════════════════
// minimum     the benchmark's own floor
// preferred   the benchmark's own ceiling (CFFM's stated objective)
// maximumSafe the most this farm can commit without being oversold on a SHORT
//             crop — RMA's own insured guarantee (position.exposure.guaranteeBu),
//             the only sourced downside production figure in this codebase.
// nextStep    ONE practical 1,000-bu lot-floored step, never the full distance.
//
// Every cap that bound the step is reported, so "why 14,000 and not 47,000"
// is answerable from the object rather than from the source.
export function resolveTargets({
  planningBu, priceProtectedBu, floorPct, ceilingPct, maximumSafeBu = null, stepPct = 0.10,
} = {}) {
  const plan = posNum(planningBu);
  if (plan == null || priceProtectedBu == null) {
    return { known: false, reason: plan == null ? "planning production not known" : "price-protected bushels not known" };
  }
  const held = Math.max(0, Number(priceProtectedBu));
  const minimumBu = floorPct != null ? (floorPct / 100) * plan : null;
  const preferredBu = ceilingPct != null ? (ceilingPct / 100) * plan : null;
  const safeBu = posNum(maximumSafeBu);

  const caps = [];
  const distanceExact = preferredBu != null ? Math.max(0, preferredBu - held) : null;
  if (distanceExact != null) caps.push({ name: "distance to the preferred target", bu: distanceExact });
  const stepCap = roundDownToLot(Math.round(plan * stepPct));
  if (stepCap > 0) caps.push({ name: `the one-step cap of ${Math.round(stepPct * 100)}% of planning production`, bu: stepCap });
  if (safeBu != null) caps.push({ name: "headroom to the maximum safe commitment", bu: Math.max(0, safeBu - held) });

  const binding = caps.length ? caps.reduce((a, b) => (b.bu < a.bu ? b : a)) : null;
  // Rounded DOWN, always — a recommendation never asks for more than the
  // deterministic calculation intended, and a sub-lot amount floors to a real
  // zero rather than inventing a 1,000-bu lot.
  const nextStepBu = binding ? roundDownToLot(Math.floor(binding.bu)) : null;

  return {
    known: true,
    minimum: minimumBu != null ? { pct: floorPct, bu: minimumBu, class: CLASS.MODEL_BENCHMARK } : null,
    preferred: preferredBu != null ? { pct: ceilingPct, bu: preferredBu, class: CLASS.MODEL_BENCHMARK } : null,
    maximumSafe: safeBu != null
      ? {
          bu: safeBu, pct: +(safeBu / plan * 100).toFixed(1), class: CLASS.MODEL_BENCHMARK,
          basis: "RMA's own insured guarantee for this crop and year — the sourced downside production figure",
        }
      : null,
    // The FULL distance to the preferred target, named as a distance and
    // never as a recommendation.
    fullDistanceToPreferredBu: distanceExact != null ? roundDownToLot(Math.floor(distanceExact)) : null,
    fullDistanceToPreferredExactBu: distanceExact,
    nextStepBu,
    nextStepBoundBy: binding ? binding.name : null,
    caps,
    subLot: distanceExact != null && distanceExact > 0 && nextStepBu === 0,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 5. THE FUTURES READ — one comparison, expressed in years, never a percentile
// ═══════════════════════════════════════════════════════════════════════════
// TWO differently-defined percentiles were on the page at once and neither
// said which it was:
//   - every forward-curve tile printed `STRONG · 100th` — that contract's own
//     LIFE-TO-DATE percentile. Structurally 100th for every live contract in
//     this archive: futures_contract_history stores each HISTORICAL contract
//     as a one-year segment (CZ16..CZ25 all hold ~250 bars) while a LIVE
//     contract carries its whole trading life from first listing (CZ26 holds
//     789 bars back to 2023-07-10). That window opens in the 2023-24 low, so
//     today is its maximum by construction — measured 2026-08-28, all five
//     corn tiles (CZ26/CH27/CK27/CN27/CU27) read 100th on the same day.
//   - the big gauge printed `STRONG · 76th percentile` — the CROSS-YEAR,
//     same-contract-month, same-point-before-delivery score. A different,
//     better-defined population, and a different number, unlabelled.
//
// The Summary carries ONE read and it is the cross-year one, expressed as a
// RANK AMONG YEARS. Six or seven comparable years do not support a
// percentile: scoreContractAtLifePoint computes its percentile over daily
// BARS (n=128 across 6 years for CZ26 on 2026-08-28), and the years do not
// even contribute equally — measured on CU27 the same day, 2026 supplied 22
// of 57 observations against 6 for 2023, because the one-year historical
// segments barely reach an offset over a year out. A bar-count percentile
// over an unequally-weighted sample is precision the data has not got.
//
// So: one observation per comparable year (that year's MEDIAN settle inside
// the same window), today ranked among them.
export const FUTURES_READ = {
  ELEVATED: "historically_elevated",
  ABOVE: "above_average",
  MIDDLE: "middle_of_range",
  BELOW: "below_average",
  WEAK: "historically_weak",
  LIMITED: "limited_history",
  NONE: "no_reliable_comparison",
};

const FUTURES_READ_LABEL = {
  [FUTURES_READ.ELEVATED]: "Historically elevated",
  [FUTURES_READ.ABOVE]: "Above average",
  [FUTURES_READ.MIDDLE]: "Middle of comparable range",
  [FUTURES_READ.BELOW]: "Below average",
  [FUTURES_READ.WEAK]: "Historically weak",
  [FUTURES_READ.LIMITED]: "Limited history",
  [FUTURES_READ.NONE]: "No reliable comparison",
};

// MIN_COMPARABLE_YEARS is a STATED PARAMETER, not an economic threshold: five
// prior years plus today is the smallest set on which "3rd highest of 6" says
// anything a reader can act on. Below it the read is LIMITED and prints the
// real count — never a category.
export const MIN_COMPARABLE_YEARS = 5;

export function summarizeFuturesRead({ contract = null, yearRank = null, trend = null, asOf = null } = {}) {
  const base = {
    contract: contract || null, asOf: asOf || null,
    // Trend is ALWAYS separate from level. A historically elevated price that
    // has begun falling and one still rising are different reads, and one
    // word cannot carry both.
    trend: trend || { known: false, direction: null, label: "Trend not measured", sessions: null },
    class: CLASS.MARKET_OBSERVED,
    price: contract && contract.last != null ? contract.last : null,
  };
  if (!yearRank || !yearRank.available) {
    return {
      ...base, read: FUTURES_READ.NONE, label: FUTURES_READ_LABEL[FUTURES_READ.NONE],
      comparison: yearRank || null, rankLabel: null,
      reason: (yearRank && yearRank.reason) || "no cross-year comparison resolved for this contract month",
    };
  }
  const n = yearRank.totalYears;   // comparable years + today
  const r = yearRank.rank;         // 1 = highest
  if (yearRank.comparableYears < MIN_COMPARABLE_YEARS) {
    return {
      ...base, read: FUTURES_READ.LIMITED, label: FUTURES_READ_LABEL[FUTURES_READ.LIMITED],
      comparison: yearRank, rankLabel: null,
      reason: `only ${yearRank.comparableYears} comparable year${yearRank.comparableYears === 1 ? "" : "s"} of this contract month at this point before delivery — under ${MIN_COMPARABLE_YEARS}, a rank says more about the sample than the market`,
    };
  }
  // Share of comparable years sitting BELOW today. Stated bands.
  const below = n - r;
  const f = below / (n - 1);
  const read = f >= 0.85 ? FUTURES_READ.ELEVATED
    : f >= 0.60 ? FUTURES_READ.ABOVE
    : f >= 0.40 ? FUTURES_READ.MIDDLE
    : f >= 0.15 ? FUTURES_READ.BELOW
    : FUTURES_READ.WEAK;
  return {
    ...base,
    read, label: FUTURES_READ_LABEL[read],
    comparison: yearRank,
    // "Highest of 7" / "3rd highest of 7" — the whole claim, in the units the
    // sample actually supports.
    rankLabel: `${ordinal(r)} of ${n} comparable year${n === 1 ? "" : "s"} at this point before delivery`,
    shareBelow: +f.toFixed(3),
    reason: null,
  };
}

function ordinal(n) {
  const v = Number(n);
  if (v === 1) return "Highest";
  const s = ["th", "st", "nd", "rd"], m = v % 100;
  return v + (s[(m - 20) % 10] || s[m] || s[0]) + " highest";
}

// sessionTrend — Rising / Flat / Falling over a STATED number of sessions.
// The flat band is a stated parameter (0.5% of price), not a finding.
export function sessionTrend(closes, sessions = 20, flatBandPct = 0.005) {
  const s = (closes || []).filter((c) => c != null && Number.isFinite(Number(c))).map(Number);
  if (s.length < sessions + 1) {
    return { known: false, direction: null, label: "Trend not measured", sessions, reason: `fewer than ${sessions + 1} sessions on file` };
  }
  const last = s[s.length - 1], prior = s[s.length - 1 - sessions];
  const chg = last - prior;
  const band = Math.abs(prior) * flatBandPct;
  const direction = chg > band ? "rising" : (chg < -band ? "falling" : "flat");
  return {
    known: true, direction,
    label: `${direction === "rising" ? "Rising" : direction === "falling" ? "Falling" : "Flat"} over the last ${sessions} sessions`,
    sessions, changed: +chg.toFixed(4), from: prior, to: last, flatBandPct,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 6. SILAS DOES NOT TELL A FARMER WHERE TO SELL (Kevin, 2026-08-28 — standing)
// ═══════════════════════════════════════════════════════════════════════════
// "The Grain Marketing Summary must never recommend, select or imply a
// preferred elevator, buyer or delivery destination." Silas lays out the facts
// — posted cash, the futures and basis components, the bid STRUCTURE, the
// delivery period, the quote date and freshness, freight when it is actually
// known, net-to-farm ONLY when those costs are known, comparable basis history,
// contract requirements, and whether the destination is confirmed usable by the
// farm — and the farmer and agent decide the counterparty.
//
// FORBIDDEN, in these words and in effect: best elevator, best place to sell,
// recommended destination, "sell to X", "deliver to Y", highest bid wins,
// preferred buyer. And no silent equivalent: **no automatic selection of one
// cash bid for the recommendation engine**, and no ranking of DELIVERED against
// PICKED-UP or DIRECT-SHIP bids as if they were economically comparable —
// they are not, until freight and logistics are verified.
//
// This replaced a weaker rule of my own from earlier the same day. That version
// still SELECTED a bid (the allowlist's top rank, then price) and merely
// labelled it "example" — which is the same recommendation with a hedge on it.
// It also drew every posted bid on ONE price-sorted bar scale with delivered
// and picked-up mixed together, which is precisely the equivalence the rule
// forbids. Measured 2026-08-28: that ordering put WNY Energy (Medina) top at
// $5.44 against Howlett LeRoy's $5.24 — and ~25-30c of that 20c gap is the
// round trip, so the "top" bid nets LESS. A price sort across structures is not
// a ranking of anything a farmer can act on.
//
// SIX conditions, all required, before a bid may be shown as this FARM's own.
// Geography is explicitly NOT one of them: a farm may never deliver to the
// geographically closest or nominally highest bidder.
export const DESTINATION_GATE = [
  "destinationConfirmed",    // an explicit, agent-confirmed destination for THIS farm
  "structureMatches",        // pickup / delivered / direct-ship matches how this farm moves grain
  "deliveryPeriodMatches",   // the bid's delivery window is the one being priced
  "futuresReferenceMatches", // the bid is quoted against the contract being read
  "bidCurrent",              // the bid is dated and current
  "logisticsKnown",          // haul/freight arrangement is known
];

export function resolveFarmDestination({ cash = null, farmerProfile = null, asOf = null, maxBidAgeDays = 7 } = {}) {
  const checks = {};
  const confirmed = !!(farmerProfile && Array.isArray(farmerProfile.allowedSources)
    && cash && cash.source && farmerProfile.allowedSources.includes(cash.source));
  checks.destinationConfirmed = confirmed;
  checks.structureMatches = confirmed && !!(farmerProfile.bidStructure && cash
    && farmerProfile.bidStructure === cash.bidStructure);
  checks.deliveryPeriodMatches = !!(cash && cash.deliveryPeriod);
  checks.futuresReferenceMatches = !!(cash && cash.deliveryContract);
  const ageDays = (cash && cash.asOf && asOf) ? daysBetween(cash.asOf, asOf) : null;
  checks.bidCurrent = ageDays != null && ageDays >= 0 && ageDays <= maxBidAgeDays;
  checks.logisticsKnown = !!(farmerProfile && farmerProfile.freightKnown === true);

  const failed = DESTINATION_GATE.filter((k) => !checks[k]);
  if (failed.length) {
    return {
      available: false, checks, failed,
      class: CLASS.UNKNOWN,
      // Kevin's own wording, 2026-08-28. Note it says EXECUTION, not "basis":
      // the earlier "Farm-specific basis: not available" still implied Silas had
      // a destination in mind and merely could not price it.
      headline: "Farm-specific cash execution not evaluated",
      reason: confirmed
        ? "a destination is on file for this farm, but the posted bid does not yet satisfy every execution condition"
        : "Regional bids are available on Cash Board, but Silas does not know which buyers, delivery terms or logistics are practical for this farm.",
      // Deliberately carries NO source name, NO price and NO basis. A named
      // elevator here is exactly the claim this rule exists to refuse.
      bidAgeDays: ageDays,
      basisAttractive: null,
    };
  }
  // Confirmed — and even here Silas states FACTS, never a preference. Net-to-farm
  // appears ONLY when a real freight figure was supplied; an unknown haul cost
  // leaves it null rather than quietly treating the posted bid as the net.
  const bc = cash.basisContext;
  const freight = (farmerProfile && Number.isFinite(Number(farmerProfile.freightPerBu)))
    ? Number(farmerProfile.freightPerBu) : null;
  return {
    available: true, checks, failed: [],
    class: CLASS.MARKET_OBSERVED,
    headline: "Confirmed destination — posted terms",
    source: cash.source, sourceLabel: cash.sourceLabel,
    basis: cash.basis, price: cash.price, bidStructure: cash.bidStructure,
    deliveryPeriod: cash.deliveryPeriod, deliveryContract: cash.deliveryContract,
    futures: cash.futures ?? null,
    asOf: cash.asOf, bidAgeDays: ageDays,
    freightPerBu: freight,
    netToFarm: freight != null && cash.price != null ? +(cash.price - freight).toFixed(4) : null,
    netToFarmNote: freight != null
      ? "Based on the entered freight assumption, this is the estimated net price."
      : "Net-to-farm not shown — no freight or hauling cost is on file.",
    basisAttractive: bc && bc.available ? bc.context === "strong" : null,
    basisContext: bc || null,
  };
}

// ── resolveBidPresentation — posted bids, NEUTRALLY ────────────────────────
// One group per (bid structure x delivery period). Inside a group the bids ARE
// comparable and are listed in a stated order; ACROSS groups nothing is ranked,
// nothing is scaled against a shared maximum, and no bid is marked selected.
//
// `orderedBy` is returned so the UI states the ordering rather than implying a
// judgement, and `comparable:false` groups (an unknown structure) say so.
// There is no "top", "best" or "winner" field on this object, by construction —
// a caller that wants one has to invent it, which a checker can see.
export function resolveBidPresentation({ candidates = [], asOf = null, maxBidAgeDays = 7 } = {}) {
  const rows = (candidates || []).filter((c) => c && c.price != null);
  if (!rows.length) {
    return { known: false, reason: "no posted cash bid resolved for this delivery period", groups: [] };
  }
  const byKey = new Map();
  for (const c of rows) {
    const structure = c.bidStructure || "unknown";
    const period = c.deliveryPeriod || "delivery period not stated";
    const key = structure + "|" + period;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key, structure, period,
        // Delivered, picked-up and direct-ship bids are NOT economically
        // equivalent until freight and logistics are verified, so they never
        // share a scale, an ordering or a bar.
        comparable: structure === "delivered" || structure === "pickup" || structure === "direct_ship",
        bids: [],
      });
    }
    const age = (c.asOf && asOf) ? daysBetween(c.asOf, asOf) : null;
    byKey.get(key).bids.push({
      sourceLabel: c.sourceLabel || c.source || "source not stated",
      price: c.price, basis: c.basis ?? null,
      futures: c.futures ?? null,
      deliveryContract: c.deliveryContract || null,
      asOf: c.asOf || null, ageDays: age,
      stale: !(age != null && age >= 0 && age <= maxBidAgeDays),
      // Never a net price unless a real freight figure exists for this farm and
      // this bid. None does today, so this is null on every row.
      netToFarm: null,
    });
  }
  const groups = [...byKey.values()].map((g) => ({
    ...g,
    // A stated ordering INSIDE one structure and one delivery period, where the
    // bids genuinely are like-for-like. Never across groups.
    bids: g.bids.slice().sort((a, b) => b.price - a.price),
    orderedBy: "posted cash price, highest first — within this structure and delivery period only",
  })).sort((a, b) => (a.structure + a.period).localeCompare(b.structure + b.period));
  return {
    known: true, groups,
    note: "Posted terms only. Delivered, picked-up and direct-ship bids are listed separately and are not compared with each other — freight, shrink, grade, drying, wait time, contract terms and operational fit are not in these numbers.",
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 7. HARVEST LOGISTICS — a delivery commitment only covers harvest if it
//    DELIVERS AT HARVEST
// ═══════════════════════════════════════════════════════════════════════════
// grainHarvestExecution.js credits `commitment.physicalBu` — EVERY contract,
// whatever its delivery window — against the bushels storage cannot hold.
// Measured on Meisenzahl Farms CY2026 corn, 2026-08-28: 62,000 bu contracted,
// of which 25,000 deliver in February and March 2027. Those bushels have to
// be STORED first; they cannot also be the reason storage is enough. The page
// therefore read "HARVEST OUTLET COVERED" against a real ~8,500 bu shortfall.
//
// THE HARVEST-DELIVERY WINDOW IS DERIVED FROM TWO FACTS THE REPO ALREADY
// HOLDS, and both are named on screen rather than assumed: the crop's
// harvest-start convention (grainMarketingStagePolicy.js's own disclosed
// per-crop date, explicitly NOT a CFFM figure) and the crop's new-crop board
// month (Dec corn, Nov soybeans, Jul wheat), whose end is where
// harvest-period delivery stops being harvest-period delivery.
//
// A CONTRACT WITH NO DELIVERY WINDOW IS UNKNOWN, NEVER APPLICABLE AND NEVER
// INAPPLICABLE. Unknown bushels are counted and reported, and where they
// could close the gap the gap is returned as a RANGE rather than a number.
export function harvestDeliveryWindow({ crop, cropYear, harvestStartMonthDay, newCropMonth } = {}) {
  const y = Number(cropYear);
  if (!y || !Array.isArray(harvestStartMonthDay) || !newCropMonth) return null;
  const [sm, sd] = harvestStartMonthDay;
  const start = `${y}-${pad2(sm)}-${pad2(sd)}`;
  // Last day of the new-crop delivery month. A contract delivering after it is
  // a stored-grain contract, not a harvest outlet.
  const endMonth = Number(newCropMonth);
  const endDay = new Date(Date.UTC(y, endMonth, 0)).getUTCDate();
  const end = `${y}-${pad2(endMonth)}-${pad2(endDay)}`;
  return {
    start, end, crop, cropYear: y,
    basis: "this crop's harvest-start convention (grainMarketingStagePolicy.js — this repo's own disclosed per-crop date, not a CFFM figure) through the end of its new-crop delivery month",
  };
}

function unknownLogistics(reason) {
  return {
    known: false, reason, class: CLASS.UNKNOWN,
    planningBu: null, storageCapacityBu: null, storageMode: null,
    harvestOutletRequiredBu: null, storageOptionalityBu: null, applicableCommitmentBu: null,
    laterDeliveryBu: null, unknownWindowBu: null, harvestOutletGapBu: null,
    harvestOutletGapLowerBu: null, gapIsRange: false, window: null,
    counts: { applicable: 0, later: 0, undated: 0 },
  };
}

export function resolveHarvestLogistics({
  planningBu, storageCapacityBu, storageMode, contracts = [], window: win = null,
} = {}) {
  const plan = posNum(planningBu);
  if (plan == null) return unknownLogistics("planning production not known");
  if (storageMode == null) return unknownLogistics("storage strategy has not been chosen for this farm");
  const capacity = storageMode === "deliver_at_harvest" ? 0 : posNum(storageCapacityBu);
  if (capacity == null) return unknownLogistics("on-farm storage capacity has not been stated");

  const requiredBu = Math.max(0, plan - capacity);
  const storableBu = Math.min(plan, capacity);

  if (!win) {
    return {
      ...unknownLogistics("harvest-delivery window could not be derived for this crop"),
      planningBu: plan, storageCapacityBu: capacity, storageMode,
      harvestOutletRequiredBu: requiredBu, storageOptionalityBu: storableBu,
    };
  }

  let applicableBu = 0, laterBu = 0, unknownWindowBu = 0;
  let nApplicable = 0, nLater = 0, nUndated = 0;
  for (const c of contracts || []) {
    const bu = Number(c && c.bushels_contracted) || 0;
    if (!(bu > 0)) continue;
    const s = ymd(c.delivery_start), e = ymd(c.delivery_end);
    if (!s && !e) { unknownWindowBu += bu; nUndated++; continue; }
    const cs = s || e, ce = e || s;
    // Overlap with the harvest-delivery window.
    if (cs <= win.end && ce >= win.start) { applicableBu += bu; nApplicable++; }
    else { laterBu += bu; nLater++; }
  }

  const gapBu = Math.max(0, requiredBu - applicableBu);
  const gapLowerBu = Math.max(0, requiredBu - applicableBu - unknownWindowBu);
  return {
    known: true, reason: null,
    class: CLASS.FARM_FACT,
    planningBu: plan, storageCapacityBu: capacity, storageMode,
    harvestOutletRequiredBu: requiredBu,
    storageOptionalityBu: storableBu,
    applicableCommitmentBu: applicableBu,
    laterDeliveryBu: laterBu,
    unknownWindowBu,
    harvestOutletGapBu: gapBu,
    // When some contracts carry no delivery window the gap is a range, not a
    // number. Equal bounds mean every contract was dated.
    harvestOutletGapLowerBu: gapLowerBu,
    gapIsRange: unknownWindowBu > 0 && gapLowerBu !== gapBu,
    window: win,
    counts: { applicable: nApplicable, later: nLater, undated: nUndated },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 8. THE FOUR EXPOSURE LANES
// ═══════════════════════════════════════════════════════════════════════════
// A farm can need four different actions and they are not the same action.
// The old card collapsed every one of them into "OPPORTUNITY SALE".
//
// The position segments (what IS committed) come straight off
// grainCommitment.js and are NOT re-derived here. The lanes (what is still
// EXPOSED) are sums over those segments, and the two must reconcile: every
// segment plus the uncommitted remainder equals planning production exactly.
export function resolveExposureLanes({ commitment = null, planningBu = null, harvest = null } = {}) {
  const plan = posNum(planningBu);
  if (!commitment || commitment.physicalBu == null || plan == null) {
    return {
      known: false,
      reason: !commitment || commitment.physicalBu == null
        ? "no contract record on file for this crop and year — committed bushels are UNKNOWN, not zero"
        : "planning production not known",
      segments: null, lanes: null,
    };
  }
  const cashPriced = Number(commitment.cashPricedBu) || 0;
  const futuresProtected = Number(commitment.futuresProtectedBu) || 0;
  const basisProtected = Number(commitment.basisProtectedBu) || 0;
  const physicalOnly = Number(commitment.physicalOnlyBu) || 0;
  const unknownProtection = Number(commitment.unknownProtectionBu) || 0;
  const physical = Number(commitment.physicalBu) || 0;
  // NOT clamped: a farm committed beyond its planning crop is the single
  // exposure this page exists to surface, and clamping it to 100% hides it.
  const noCommitment = Math.max(0, plan - physical);
  const overCommittedBu = Math.max(0, physical - plan);

  const segments = [
    { key: "cash_priced", label: "Cash-priced", bu: cashPriced, class: CLASS.FARM_FACT },
    { key: "futures_protected", label: "Futures protected / basis open", bu: futuresProtected, class: CLASS.FARM_FACT },
    { key: "basis_protected", label: "Basis protected / futures open", bu: basisProtected, class: CLASS.FARM_FACT },
    { key: "physical_only", label: "Physical commitment only", bu: physicalOnly, class: CLASS.FARM_FACT },
    { key: "unknown_protection", label: "Protection not stated", bu: unknownProtection, class: CLASS.UNKNOWN },
    { key: "no_commitment", label: "No commitment", bu: noCommitment, class: CLASS.MODEL_BENCHMARK },
  ];

  const lanes = {
    // FUTURES: everything whose futures-direction risk is still open.
    futures: {
      key: "futures", label: "No futures-price protection",
      bu: noCommitment + basisProtected + physicalOnly + unknownProtection,
      action: "Review new futures protection.",
    },
    // BASIS: futures fixed, basis floating. An HTA's own remainder.
    basis: {
      key: "basis", label: "Futures fixed, basis open",
      bu: futuresProtected,
      action: "Review fixing basis on existing HTA bushels.",
    },
    // CASH PRICE: physically committed with no final cash price on them.
    cashPrice: {
      key: "cash_price", label: "Committed without a final cash price",
      bu: Math.max(0, physical - cashPriced),
      action: "Complete the remaining price component.",
    },
    // PHYSICAL OUTLET: nowhere to go at harvest. Independent of price.
    physicalOutlet: {
      key: "physical_outlet", label: "No harvest destination",
      bu: harvest && harvest.known ? harvest.harvestOutletGapBu : null,
      unknown: !(harvest && harvest.known),
      reason: harvest && !harvest.known ? harvest.reason : null,
      action: "Arrange a harvest outlet independent of price.",
    },
  };

  // The one identity the page must never break.
  const segmentSum = segments.reduce((s, x) => s + x.bu, 0);
  return {
    known: true, segments, lanes,
    physicalBu: physical, planningBu: plan,
    overCommittedBu,
    pctPriceProtected: +((cashPriced + futuresProtected) / plan * 100).toFixed(1),
    pctCashPriced: +(cashPriced / plan * 100).toFixed(1),
    pctPhysical: +(physical / plan * 100).toFixed(1),
    reconciles: Math.abs(segmentSum - plan) < 0.5,
    segmentSum,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// 9. FACTS TO REVIEW — SILAS DOES NOT TELL A FARMER WHEN TO SELL
// ═══════════════════════════════════════════════════════════════════════════
// Kevin, 2026-09-01, standing rule alongside the destination rule:
//
//   1. Silas does not tell farmers WHEN to sell.
//   2. Silas does not tell farmers WHERE to sell.
//   3. Silas presents the relevant facts clearly and lets the farmer decide.
//
// ⚠ WHAT THIS REPLACED WAS MINE, FROM THREE DAYS EARLIER. resolvePrimaryDecision()
// emitted exactly one instruction per farm — REVIEW FUTURES PROTECTION, REVIEW
// OPEN BASIS, NO NEW COMMITMENT, WATCH MARKET — and its statement read "Review
// one 14,000 bu futures-protection step." I believed "review" was a neutral
// enough verb. It is not: the page picked ONE action out of several, ordered the
// others away, and sized it. A prescriptive hedge carrying a quantity is a
// marketing decision however softly it is worded.
//
// The replacement states the same underlying facts and stops. Every entry is a
// measured value already on this page, phrased as an observation with no verb
// pointed at the reader:
//
//   63.3% of planning production has no confirmed futures-price protection.
//   Dec 26 futures are $5.37.
//   That is the 3rd highest of 7 comparable historical observations.
//   No farmer-selected futures targets are currently working.
//   Delivery and logistics information is incomplete.
//
// THE TESTS THIS MUST PASS, asserted in check-grain-summary.js:
//   - no imperative verb aimed at the reader (sell, hedge, price, protect, buy,
//     move, deliver, lock, act, wait, hold);
//   - no bushel quantity presented as a step, tranche or amount to market;
//   - no ranking of the facts into a single "primary" one;
//   - a benchmark may be named, but must be labelled a MODEL benchmark rather
//     than the farm's own objective.
//
// A gauge elsewhere may summarise market conditions relative to historical
// context. It may never mean "the probability that selling today is correct".

// FACT_KIND groups the lines so a caller can style them, NOT rank them. There is
// deliberately no priority field and no single primary fact — the order here is
// the order a reader asks the questions in, and swapping two lines changes
// nothing about what Silas is claiming.
export const FACT_KIND = {
  POSITION: "position",
  MARKET: "market",
  ECONOMICS: "economics",
  FARMER_TARGETS: "farmer_targets",
  MISSING: "missing",
};

function pctOf(a, b) { return b > 0 ? (a / b * 100).toFixed(1) + "%" : "—"; }
function money(v) { return v == null ? "—" : "$" + Number(v).toFixed(2); }
function n0(v) { return Math.round(Number(v) || 0).toLocaleString(); }

// resolveFactsToReview — every line is a statement about a measured value.
//
// farmerTargets: the farm's OWN established targets, or [] when none are
//   working, or null when the record is not wired into this view. Silas
//   administers these; it never invents one. Null and empty are different
//   claims and are worded differently.
// breakeven: { perBu, farmSpecific } — farmSpecific false means a model or book
//   default, and the sentence says so. A default cost structure must never read
//   as the farmer's own COP.
export function resolveFactsToReview({
  production, lanes, harvest, futures, destination, range,
  farmerTargets = null, breakeven = null,
} = {}) {
  const facts = [];
  const add = (kind, text, evidence) => facts.push({ kind, text, evidence: evidence || null, class: CLASS.MARKET_OBSERVED });

  // ── position
  if (!production || !production.known) {
    add(FACT_KIND.MISSING, "Planning production is not established for this crop and year, so no percentage of the crop can be stated.");
  } else if (!lanes || !lanes.known) {
    add(FACT_KIND.MISSING, (lanes && lanes.reason) || "No grain contract record is on file for this crop and year, so committed bushels are unknown — not zero.");
  } else {
    const unprot = lanes.lanes.futures.bu;
    const cashSeg = lanes.segments.find((s) => s.key === "cash_priced") || { bu: 0 };
    const futSeg = lanes.segments.find((s) => s.key === "futures_protected") || { bu: 0 };
    add(FACT_KIND.POSITION,
      pctOf(unprot, lanes.planningBu) + " of planning production has no confirmed futures-price protection (" + n0(unprot) + " bu of " + n0(lanes.planningBu) + " bu).",
      { field: "lanes.futures.bu", value: unprot });
    add(FACT_KIND.POSITION,
      "Confirmed price protection: " + lanes.pctPriceProtected + "% — " + n0(cashSeg.bu) + " bu cash-priced and " + n0(futSeg.bu) + " bu with futures locked and basis open.",
      { field: "lanes.pctPriceProtected", value: lanes.pctPriceProtected });
    if (lanes.overCommittedBu > 0) {
      add(FACT_KIND.POSITION,
        "Physical commitment exceeds planning production by " + n0(lanes.overCommittedBu) + " bu (" + lanes.pctPhysical + "% of the crop).",
        { field: "lanes.overCommittedBu", value: lanes.overCommittedBu });
    }
  }

  // ── market. Level and trend are stated separately, and the sample size rides
  // on the line rather than hiding behind an adjective.
  if (futures && futures.contract && futures.price != null) {
    add(FACT_KIND.MARKET, futures.contract.label + " futures are " + money(futures.price) + ".",
      { field: "futures.price", value: futures.price, asOf: futures.contract.asOf });
    if (futures.rankLabel) {
      add(FACT_KIND.MARKET, "That is the " + futures.rankLabel.toLowerCase() + ".",
        { field: "futures.comparison", value: futures.comparison ? futures.comparison.rank : null });
    } else if (futures.reason) {
      add(FACT_KIND.MISSING, "No reliable cross-year comparison for " + futures.contract.label + ": " + futures.reason);
    }
    if (futures.trend && futures.trend.known) {
      add(FACT_KIND.MARKET, futures.trend.label + ".", { field: "futures.trend", value: futures.trend.direction });
    }
  }

  // ── economics. The difference is a fact; whether it is worth acting on is not
  // Silas's call, so the line states it and stops.
  if (breakeven && breakeven.perBu != null && futures && futures.price != null) {
    const diff = futures.price - breakeven.perBu;
    add(FACT_KIND.ECONOMICS,
      (futures.contract ? futures.contract.label : "Futures") + " are " + money(Math.abs(diff)) + "/bu "
      + (diff >= 0 ? "above" : "below") + " the " + (breakeven.farmSpecific ? "farm-entered" : "modelled") + " break-even of " + money(breakeven.perBu) + "/bu.",
      { field: "breakeven.perBu", value: breakeven.perBu, farmSpecific: !!breakeven.farmSpecific });
  }

  // ── the farmer's own decisions. These are the only levels on this page the
  // farmer can act on, because the farmer chose them.
  const ft = Array.isArray(farmerTargets) ? farmerTargets : null;
  if (ft == null) {
    add(FACT_KIND.MISSING, "Farmer-selected futures targets are not wired into this view yet, so none can be shown.");
  } else if (!ft.length) {
    add(FACT_KIND.FARMER_TARGETS, "No farmer-selected futures targets are currently working.");
  } else {
    for (const t of ft) {
      add(FACT_KIND.FARMER_TARGETS,
        (t.contractLabel || "Board") + " · " + money(t.price) + " · " + n0(t.bushels) + " bu · " + String(t.status || "working").toUpperCase() + ".",
        { field: "farmerTarget", value: t.price, status: t.status });
    }
  }

  // ── the model benchmark, LABELLED as one. Distance is context; there is no
  // step, no quantity to market, and no verb.
  if (range && range.known && lanes && lanes.known) {
    add(FACT_KIND.POSITION,
      "Model planning benchmark for this crop and stage is " + range.floorPct + "%–" + range.ceilingPct + "%; confirmed protection is "
      + lanes.pctPriceProtected + "%, " + Math.abs(range.distanceToCeilingPct) + " points "
      + (range.distanceToCeilingPct >= 0 ? "below" : "above") + " the top of that band. This is a model benchmark, not a target the farm has chosen.",
      { field: "range", value: lanes.pctPriceProtected });
  }

  // ── logistics
  if (harvest && harvest.known && harvest.harvestOutletGapBu > 0) {
    add(FACT_KIND.POSITION,
      n0(harvest.harvestOutletGapBu) + " bu exceed on-farm storage and the delivery commitments that deliver during the harvest window ("
      + harvest.window.start + " to " + harvest.window.end + ").",
      { field: "harvest.harvestOutletGapBu", value: harvest.harvestOutletGapBu });
  } else if (!harvest || !harvest.known) {
    add(FACT_KIND.MISSING, "Harvest logistics are incomplete — "
      + ((harvest && harvest.reason) || "storage capacity and intended delivery outlets are not established")
      + ". Futures-market context above is unaffected.");
  }
  if (!destination || !destination.available) {
    add(FACT_KIND.MISSING, "Delivery and logistics information is incomplete, so no farm-specific cash or basis context can be shown.");
  }

  return { facts, count: facts.length };
}

// ═══════════════════════════════════════════════════════════════════════════
// 10. WHAT CHANGES THIS READ — at most three, every one deterministic
// ═══════════════════════════════════════════════════════════════════════════
// A trigger must be re-derivable from the numbers on this page. No invented
// resistance level, no round-number price, no "if the market rallies".
export function resolveChangeTriggers({ production, lanes, targets, futures, harvest, destination } = {}) {
  const out = [];
  const plan = production && production.known ? production.bu : null;

  if (harvest && harvest.known && harvest.harvestOutletGapBu > 0) {
    out.push({
      text: `Entering ${n0(harvest.harvestOutletGapBu)} bu of additional storage — or contracting that many bushels for delivery inside ${harvest.window.start} to ${harvest.window.end} — removes the harvest-outlet gap.`,
      basis: "planning production minus storage, minus contracts delivering in the harvest window",
      class: CLASS.MODEL_BENCHMARK,
    });
  }

  if (lanes && lanes.known && targets && targets.known && targets.nextStepBu > 0 && plan) {
    const after = ((lanes.pctPriceProtected / 100) * plan + targets.nextStepBu) / plan * 100;
    out.push({
      text: `Another ${n0(targets.nextStepBu)} bu protected would move futures protection from ${lanes.pctPriceProtected}% to ${after.toFixed(1)}%.`,
      basis: "price-protected bushels plus the next step, over planning production",
      class: CLASS.MODEL_BENCHMARK,
    });
  }

  if (futures && futures.comparison && futures.comparison.available && futures.comparison.nextYearBelow) {
    out.push({
      text: `If ${futures.contract && futures.contract.label ? futures.contract.label : "this contract"} settles below ${fmtPrice(futures.comparison.nextYearBelow.median)}, ${futures.comparison.nextYearBelow.year} moves above it and the "${futures.label.toLowerCase()}" classification changes.`,
      basis: `the next comparable year's median settle inside the same +/-${futures.comparison.windowDays}-day window before delivery`,
      class: CLASS.MODEL_BENCHMARK,
    });
  }

  if (destination && !destination.available && lanes && lanes.known && lanes.lanes.basis.bu > 0) {
    out.push({
      text: `A confirmed delivery destination would let Silas evaluate basis on the ${n0(lanes.lanes.basis.bu)} bu whose futures price is already fixed.`,
      basis: "no confirmed destination is on file for this farm",
      class: CLASS.UNKNOWN,
    });
  }

  if (lanes && lanes.known && lanes.physicalBu > 0) {
    out.push({
      text: `If production falls below ${n0(lanes.physicalBu)} bu, this farm is committed to deliver more than it grows.`,
      basis: "physical commitment against planning production",
      class: CLASS.MODEL_BENCHMARK,
    });
  }

  return out.slice(0, 3);
}

function fmtPrice(v) { return v == null ? "?" : "$" + Number(v).toFixed(2); }

// ═══════════════════════════════════════════════════════════════════════════
// 11. DATA QUALITY — one line, and it says what is actually limiting
// ═══════════════════════════════════════════════════════════════════════════
// Never a green tick for a proxy. "Complete" requires actual production, a
// contract record, storage, a confirmed destination and a current market read
// — which no farm in this book has today, and the line says so rather than
// awarding four ticks for four rows that came back.
export const DATA_QUALITY = {
  COMPLETE: "Complete",
  USABLE_WITH_ESTIMATES: "Usable with estimates",
  POSITION_INCOMPLETE: "Position incomplete",
  MARKET_STALE: "Market data stale",
  LOGISTICS_INCOMPLETE: "Logistics incomplete",
  NO_RELIABLE_READ: "No reliable read",
};

export function resolveDataQuality({ production, lanes, harvest, futures, destination, marketAsOf, asOf, maxMarketAgeDays = 5 } = {}) {
  if (!production || !production.known) {
    return {
      state: DATA_QUALITY.NO_RELIABLE_READ,
      limitation: "Planning production could not be resolved from actual production, the Crop Plan or RMA coverage.",
      limitations: ["Planning production unknown."], marketAgeDays: null,
    };
  }
  if (!lanes || !lanes.known) {
    return {
      state: DATA_QUALITY.POSITION_INCOMPLETE,
      limitation: "No grain contract record is on file for this crop and year, so committed bushels are unknown — not zero.",
      limitations: ["Contract position unknown."], marketAgeDays: null,
    };
  }

  const marketAge = (marketAsOf && asOf) ? daysBetween(marketAsOf, asOf) : null;
  const marketStale = marketAge == null || marketAge > maxMarketAgeDays;

  const limitations = [];
  if (production.isEstimate) {
    limitations.push(production.source === PRODUCTION_SOURCE.INSURANCE_DERIVED
      ? "Production remains insurance-derived."
      : `Production is a ${production.sourceLabel}, not a measured harvest.`);
  }
  if (!destination || !destination.available) limitations.push("Destination and basis are not confirmed.");
  if (!harvest || !harvest.known) limitations.push(`Harvest logistics are incomplete — ${(harvest && harvest.reason) || "storage is not stated"}.`);
  if (marketStale) limitations.push(`Market data is ${marketAge == null ? "undated" : marketAge + " day" + (marketAge === 1 ? "" : "s") + " old"}.`);
  if (!futures || futures.read === FUTURES_READ.NONE || futures.read === FUTURES_READ.LIMITED) {
    limitations.push("No reliable cross-year futures comparison for this contract.");
  }

  let state;
  if (marketStale) state = DATA_QUALITY.MARKET_STALE;
  else if (!harvest || !harvest.known) state = DATA_QUALITY.LOGISTICS_INCOMPLETE;
  else if (limitations.length === 0) state = DATA_QUALITY.COMPLETE;
  else state = DATA_QUALITY.USABLE_WITH_ESTIMATES;

  return {
    state,
    limitation: ["Position complete through imported contracts.", ...limitations].join(" "),
    limitations,
    marketAgeDays: marketAge,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// buildGrainSummary — the whole view-model, composed once.
// ═══════════════════════════════════════════════════════════════════════════
export function buildGrainSummary({
  crop, cropYear, asOf,
  position,                 // grainPosition.js buildGrainPosition() output
  action,                   // grainAction.js buildGrainAction() output (storage/cash only)
  contracts = [],           // this crop-year's grain_contracts rows for THIS crop
  planRow = null,           // grain_crop_plan row for this crop/year
  harvestStartMonthDay = null,
  newCropMonth = null,
  futuresContract = null,   // { sym, label, last, asOf }
  futuresYearRank = null,   // rankContractAmongYears() output for THAT contract
  futuresTrend = null,      // sessionTrend() output
  // curvePoints: every live delivery period for this crop year, each with its
  // OWN cross-year rank and its OWN trend. One methodology across the whole
  // table — a row can never be compared on a different population from the row
  // above it, which is exactly what put a life-to-date 100th beside a
  // cross-year 76th on the old card.
  curvePoints = [],
  farmerProfile = null,
  agronomicBu = null, agronomicLabel = null,
  // The farm's OWN established targets. null = the record is not wired into this
  // view; [] = none working. Silas administers these and never invents one.
  farmerTargets = null,
  // { perBu, farmSpecific } — farmSpecific false means a model/book default, and
  // the rendered sentence says so. A default must never read as the farm's COP.
  breakeven = null,
} = {}) {
  const exposure = position && position.exposure ? position.exposure : null;
  const commitment = position && position.currentProtection ? position.currentProtection.commitment : null;

  const production = resolveProductionBasis({
    actualBu: planRow ? planRow.actual_production : null,
    harvestComplete: null,           // no column states this — rank 1 is unreachable by construction
    planAcres: planRow ? planRow.acres : null,
    planYieldPerAcre: planRow ? planRow.yield_per_acre : null,
    planAphYield: planRow ? planRow.aph_yield : null,
    choppedAcres: planRow ? planRow.chopped_acres : 0,
    agronomicBu, agronomicLabel,
    insuranceDerivedBu: exposure ? exposure.expectedBu : null,
    asOf,
  });

  const stage = resolveStageLabel({ crop, cropYear, asOf });

  const storage = action && action.storage ? action.storage : null;
  const win = harvestDeliveryWindow({ crop, cropYear, harvestStartMonthDay, newCropMonth });
  const harvest = resolveHarvestLogistics({
    planningBu: production.bu,
    storageCapacityBu: storage ? storage.capacityBu : null,
    storageMode: storage ? storage.mode : null,
    contracts, window: win,
  });

  const lanes = resolveExposureLanes({ commitment, planningBu: production.bu, harvest });

  const targets = resolveTargets({
    planningBu: production.bu,
    priceProtectedBu: commitment ? commitment.priceProtectedBu : null,
    floorPct: stage.known ? stage.floorPct : null,
    ceilingPct: stage.known ? stage.ceilingPct : null,
    maximumSafeBu: exposure ? exposure.guaranteeBu : null,
  });

  const range = resolveRangePosition({
    currentPct: lanes.known ? lanes.pctPriceProtected : null,
    floorPct: stage.known ? stage.floorPct : null,
    ceilingPct: stage.known ? stage.ceilingPct : null,
  });

  const futures = summarizeFuturesRead({
    contract: futuresContract, yearRank: futuresYearRank, trend: futuresTrend,
    asOf: futuresContract ? futuresContract.asOf : null,
  });

  // `contractLabel` and `readLabel` are DELIBERATELY two names. Spreading the
  // read over `{label: p.label}` silently overwrote the contract's own label
  // with the read's, which is how a table column comes to print the wrong one
  // of two strings that are both called `label`.
  const futuresCurve = (curvePoints || [])
    .filter((p) => p && p.last != null)
    .map((p) => {
      const read = summarizeFuturesRead({
        contract: { sym: p.sym, label: p.label, last: p.last, asOf: p.asOf },
        yearRank: p.yearRank, trend: p.trend, asOf: p.asOf,
      });
      return {
        sym: p.sym, contractLabel: p.label, isAnchor: !!p.isAnchor,
        price: read.price, asOf: p.asOf, trend: read.trend,
        read: read.read, readLabel: read.label, rankLabel: read.rankLabel,
        reason: read.reason, comparison: read.comparison, class: read.class,
      };
    });

  const destination = resolveFarmDestination({
    cash: action && action.cash && action.cash.available ? action.cash : null,
    farmerProfile, asOf,
  });

  const dataQuality = resolveDataQuality({
    production, lanes, harvest, futures, destination,
    marketAsOf: futuresContract ? futuresContract.asOf : null, asOf,
  });

  // FACTS, not a decision. There is no `primary` any more: the page states what
  // is measured and the farmer decides what it means (Kevin, 2026-09-01).
  const factsToReview = resolveFactsToReview({ production, lanes, harvest, futures, destination, range, farmerTargets, breakeven });

  const triggers = resolveChangeTriggers({ production, lanes, targets, futures, harvest, destination });

  return { crop, cropYear, asOf, production, stage, range, targets, lanes, harvest, futures, futuresCurve, destination, factsToReview, triggers, dataQuality };
}
